import { useEffect, useState, type ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";
import { sessionRepository } from "@/infrastructure/repositories/session/Session.repository";
import { ensureStudioSession } from "@/infrastructure/services/auth/bootstrapSession";
import { startSessionKeepAlive } from "@/infrastructure/services/auth/sessionKeepAlive";
import { isPublicAuthRoute } from "@/auth/publicAuthRoutes";
import { useAuth } from "@/hooks/useAuth";
import {
  useDismissInitialLoaderOnError,
  useDismissInitialLoaderWhen,
  useShowInitialLoader,
} from "@/providers/AppInitialLoaderProvider";
import { Button } from "@/presentation/components/ui";

interface SessionBootstrapProps {
  children: ReactNode;
}

/**
 * Error codes that indicate the Auth0 session itself has expired or is invalid.
 * For these, we auto-logout instead of showing a manual error screen.
 */
const AUTH0_ERROR_CODES = new Set(["AUTH0_TOKEN_ERROR", "AUTH0_TOKEN_UNAVAILABLE"]);

const SessionBootstrap = ({ children }: SessionBootstrapProps) => {
  const config = WebAppConfig.getInstance();
  const { pathname } = useLocation();
  const isPublicRoute = isPublicAuthRoute(pathname);
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const { logout } = useAuth();
  const [isReady, setIsReady] = useState(
    () => !config.isAuthConfigured() || sessionRepository.hasReusableSession(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const isBootstrappingSession =
    config.isAuthConfigured() &&
    isAuthenticated &&
    !isPublicRoute &&
    !isAuth0Loading &&
    !isReady &&
    !errorMessage;

  const shouldDismissInitialLoader =
    !config.isAuthConfigured() ||
    isPublicRoute ||
    (isReady && !errorMessage);

  useShowInitialLoader(isBootstrappingSession);
  useDismissInitialLoaderWhen(shouldDismissInitialLoader);
  useDismissInitialLoaderOnError(Boolean(errorMessage));

  useEffect(() => {
    if (!isAuthenticated) {
      setIsReady(!config.isAuthConfigured());
      setErrorMessage(null);
      setErrorCode(null);
    }
  }, [config, isAuthenticated]);

  useEffect(() => {
    if (
      isPublicRoute ||
      !config.isAuthConfigured() ||
      !isAuthenticated ||
      isAuth0Loading
    ) {
      return;
    }

    if (sessionRepository.hasReusableSession()) {
      setIsReady(true);
      return;
    }

    let cancelled = false;

    void ensureStudioSession()
      .then((result) => {
        if (cancelled) return;

        if (result.ok) {
          setIsReady(true);
          setErrorMessage(null);
          setErrorCode(null);
          return;
        }

        if (AUTH0_ERROR_CODES.has(result.code ?? "")) {
          logout();
          return;
        }

        setErrorCode(result.code ?? null);
        setErrorMessage(result.message);
        setIsReady(false);
      })
      .catch(() => {
        if (!cancelled) logout();
      });

    return () => {
      cancelled = true;
    };
  }, [config, isAuthenticated, isAuth0Loading, isPublicRoute, logout]);

  useEffect(() => {
    if (
      !config.isAuthConfigured() ||
      !isAuthenticated ||
      isPublicRoute ||
      !isReady ||
      errorMessage
    ) {
      return;
    }

    return startSessionKeepAlive({
      onAuth0Expired: logout,
    });
  }, [config, errorMessage, isAuthenticated, isPublicRoute, isReady, logout]);

  if (!config.isAuthConfigured()) {
    return <>{children}</>;
  }

  if (
    isAuthenticated &&
    !isPublicRoute &&
    (isAuth0Loading || (!isReady && !errorMessage))
  ) {
    return null;
  }

  if (errorMessage && !isPublicRoute) {
    const isUnregistered = errorCode === "USER_NOT_REGISTERED";

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-lg font-semibold text-foreground">
          {isUnregistered ? "Access not granted" : "Unable to start session"}
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">
          {isUnregistered
            ? "Your account is not registered for intelliExtract Studio. Contact your administrator to request access."
            : errorMessage}
        </p>
        <Button type="button" onClick={logout}>
          Sign out
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};

export default SessionBootstrap;
